// ./src/components/features/Cars/CarTransmissionTypeSelector/CarTransmissionTypeSelector.tsx
import React, { useMemo, useCallback } from "react";
import { Select, SelectProps, Group, Text, Badge, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CarTransmissionType } from "@/utils/api";
import { useAppEssentialsContext } from "@/contexts";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

// ============================================
// Types
// ============================================

export interface CarTransmissionTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected transmission type ID */
  value?: number | string | null | undefined;

  /** Callback fired when transmission type selection changes: (name, value, transmissionType?) */
  onChange?: (
    name: string,
    value: number | null,
    transmissionType?: CarTransmissionType
  ) => void;

  /** Placeholder text when no transmission type is selected */
  placeholder?: string;

  /** Whether to include inactive transmission types in the list */
  includeInactive?: boolean;

  /** Custom filter function to filter transmission types */
  transmissionTypeFilter?: (transmissionType: CarTransmissionType) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no transmission types are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;
}

// ============================================
// Option Data Interface
// ============================================

interface TransmissionTypeOptionData {
  value: string;
  label: string;
  code?: string;
  iconName: string;
  disabled?: boolean;
}

// ============================================
// Constants - Icon mapping for transmission types
// ============================================

const TRANSMISSION_TYPE_ICONS: Record<string, string> = {
  MANUAL: "ManualGearbox",
  AUTOMATIC: "Automatic",
  CVT: "SettingsAutomation",
  DCT: "SettingsAutomation",
  AMT: "SettingsAutomation",
  SEMI: "SettingsAutomation",
};

const getTransmissionTypeIcon = (code: string | undefined): string => {
  if (!code) return "Settings";
  return TRANSMISSION_TYPE_ICONS[code.toUpperCase()] || "Settings";
};

// ============================================
// Component
// ============================================

const CarTransmissionTypeSelectorComponent: React.FC<
  CarTransmissionTypeSelectorProps
> = ({
  name = "transmissionTypeId",
  value,
  onChange,
  placeholder,
  includeInactive = false,
  transmissionTypeFilter,
  required = false,
  loadingPlaceholder,
  emptyText,
  error,
  disabled,
  size = "md",
  label,
  ...selectProps
}) => {
    const { t } = useTranslation(["cars", "common"]);
    const { appEssentials, isLoading, lastError, getCarTransmissionTypeById } =
      useAppEssentialsContext();

    const transmissionTypes = appEssentials?.carTransmissionTypes || [];

    // Default translations
    const defaultPlaceholder =
      placeholder || t("cars:selectors.transmissionType.placeholder");
    const defaultLabel = label ?? t("cars:selectors.transmissionType.label");
    const defaultLoadingPlaceholder =
      loadingPlaceholder || t("cars:selectors.transmissionType.loading");
    const defaultEmptyText =
      emptyText || t("cars:selectors.transmissionType.empty");

    // Create a map for quick transmission type lookup by ID
    const transmissionTypeMap = useMemo(() => {
      const map = new Map<number, CarTransmissionType>();
      transmissionTypes.forEach((transmissionType) => {
        if (transmissionType.id !== undefined) {
          map.set(transmissionType.id, transmissionType);
        }
      });
      return map;
    }, [transmissionTypes]);

    // Filter and prepare transmission type options
    const transmissionTypeOptions = useMemo((): TransmissionTypeOptionData[] => {
      let filteredTypes = transmissionTypes;

      // Apply custom filter if provided
      if (transmissionTypeFilter) {
        filteredTypes = filteredTypes.filter(transmissionTypeFilter);
      }

      // Convert to select options
      return filteredTypes.map((transmissionType) => ({
        value: String(transmissionType.id),
        label: transmissionType.name || "Unknown",
        code: transmissionType.code,
        iconName: getTransmissionTypeIcon(transmissionType.code),
      }));
    }, [transmissionTypes, transmissionTypeFilter]);

    // Get selected transmission type for icon display
    const selectedTransmissionType = useMemo(() => {
      if (value === null || value === undefined) return null;
      const numericValue =
        typeof value === "string" ? parseInt(value, 10) : value;
      return (
        transmissionTypeMap.get(numericValue) ||
        getCarTransmissionTypeById(numericValue)
      );
    }, [value, transmissionTypeMap, getCarTransmissionTypeById]);

    // Handle onChange with transmission type object lookup
    const handleChange = useCallback(
      (selectedValue: string | null) => {
        if (selectedValue) {
          const numericId = parseInt(selectedValue, 10);
          const transmissionType =
            transmissionTypeMap.get(numericId) ||
            getCarTransmissionTypeById(numericId);
          onChange?.(name, numericId, transmissionType);
        } else {
          onChange?.(name, null, undefined);
        }
      },
      [name, transmissionTypeMap, getCarTransmissionTypeById, onChange]
    );

    // Render option for dropdown
    const renderOption: SelectProps["renderOption"] = useCallback(
      ({ option }: any) => {
        const optionData = option as TransmissionTypeOptionData;
        return (
          <Group gap="sm" wrap="nowrap">
            <AppIcon iconName={optionData.iconName} size={20} />
            <Text size="md" fw={500}>
              {optionData.label}
            </Text>
            {optionData.code && (
              <Badge size="md" variant="light" color="gray">
                {optionData.code}
              </Badge>
            )}
          </Group>
        );
      },
      []
    );

    // Memoize left section icon
    const leftSection = useMemo(() => {
      if (!selectedTransmissionType) return undefined;
      return (
        <AppIcon
          iconName={getTransmissionTypeIcon(selectedTransmissionType.code)}
          size={18}
        />
      );
    }, [selectedTransmissionType]);

    // Determine current state
    const isEmpty = !isLoading && transmissionTypes.length === 0;

    // Memoize select state
    const selectState = useMemo(() => {
      if (isLoading && transmissionTypes.length === 0) {
        return {
          data: [] as TransmissionTypeOptionData[],
          placeholder: defaultLoadingPlaceholder,
          isDisabled: true,
          showLoading: true,
          errorMessage: undefined as string | undefined,
        };
      }

      if (lastError && !isLoading && transmissionTypes.length === 0) {
        return {
          data: [] as TransmissionTypeOptionData[],
          placeholder: t("common:errors.generic"),
          isDisabled: true,
          showLoading: false,
          errorMessage: error || t("common:messages.loadError"),
        };
      }

      if (isEmpty) {
        return {
          data: [] as TransmissionTypeOptionData[],
          placeholder: defaultEmptyText,
          isDisabled: true,
          showLoading: false,
          errorMessage: undefined as string | undefined,
        };
      }

      return {
        data: transmissionTypeOptions,
        placeholder: defaultPlaceholder,
        isDisabled: disabled || isLoading,
        showLoading: isLoading && transmissionTypes.length > 0,
        errorMessage: undefined as string | undefined,
      };
    }, [
      isLoading,
      transmissionTypes.length,
      lastError,
      isEmpty,
      transmissionTypeOptions,
      defaultLoadingPlaceholder,
      defaultEmptyText,
      defaultPlaceholder,
      disabled,
      error,
      t,
    ]);

    // Memoize right section
    const rightSection = useMemo(() => {
      if (selectState.showLoading) {
        return <LoadingIndicator size={size} color="gray" />;
      }
      return selectProps.rightSection;
    }, [selectState.showLoading, size, selectProps.rightSection]);

    return (
      <Box>
        <Select
          {...selectProps}
          label={defaultLabel}
          size={size}
          value={value !== null && value !== undefined ? String(value) : null}
          onChange={handleChange}
          placeholder={selectState.placeholder}
          clearable={!required}
          required={required}
          allowDeselect={!required}
          disabled={selectState.isDisabled}
          error={selectState.errorMessage || error}
          data={selectState.data}
          rightSection={rightSection}
          leftSection={leftSection}
          renderOption={renderOption}
          nothingFoundMessage={t("common:form.noOptions")}
          comboboxProps={DRAWER_COMBOBOX_PROPS}
        />
      </Box>
    );
  };

export const CarTransmissionTypeSelector = React.memo(
  CarTransmissionTypeSelectorComponent
);
